import {Engine} from "json-rules-engine";
import {AG_GRID_NOT_EXIST_FILTER} from "../ag-grid/AGGridUtils";
import {ColorRank} from "../../providers/DispatchRulesDataProvider";
import log from "loglevel";

// DP-2999: Client-side color sort/filter is limited to the current page (~100 rows)
// in JobsGrid. The backend rules-engine worker (follow-up ticket) will write
// indicatorRank server-side so grid datasources can sort/filter across the full
// dataset. When the `backendColorRules` ConfigCat flag is on, this helper is
// unused — the engine is not constructed — and grids read indicator fields
// straight off the GraphQL row.
export const getHighestRankedColor = (colors: string[], colorRank: ColorRank[]) => {
  colors.sort((a, b) => {
    const colorA = colorRank.find((rank: ColorRank) => rank.color === a);
    const colorB = colorRank.find((rank: ColorRank) => rank.color === b);

    return colorA && colorB ? colorA.rank - colorB.rank : 0;
  });

  return colors[0];
};

/**
 * DP-3090: runs the client-side rules engine over rebuilt grid rows — unless backend colour
 * rules are on, in which case it does nothing at all.
 *
 * That guard is the whole point of this helper existing. With the flag on, `indicatorColor`
 * arrives already stamped on each row by the colorized-indicators worker and the server is
 * authoritative; running the client engine overwrites that stamp with its own result, which
 * is `undefined` for any rule the client cannot evaluate (backend-only shapes such as the
 * DP-3065 field-to-field comparisons, or a rule whose evaluation throws). Rows are rebuilt on
 * every replication-driven refetch, so the overwrite landed on exactly the
 * assign/reassign/complete/status-change actions DP-3090 reports, and the colours did not
 * come back until the panel was reopened.
 *
 * The jobs and assigned-stops grids express the same guard by dropping their
 * buildIndicatorColors from `decorateResults` (JobsGrid.tsx, assignedstops/grid/Grid.tsx).
 * Manifest Detail rebuilds rows itself rather than going through a datasource, so it needs
 * the guard here — it was the one path that missed it.
 */
export const decorateStopIndicatorColors = async <TRow>(
  rows: TRow[],
  applyClientColor: (row: TRow) => Promise<void>,
  backendColorRulesEnabled?: boolean
): Promise<void> => {
  if (backendColorRulesEnabled) return;

  const results = await Promise.allSettled(rows.map((row) => applyClientColor(row)));

  results.forEach((result) => {
    if (result.status === "rejected") {
      log.warn("Error getting color indicator", result.reason);
    }
  });
};

export const getColorIndicator = async (engine: Engine): Promise<any> => {
  try {
    const result = await engine.run();

    if (result.events.length > 0) {
      return result.events[0].params?.color;
    }
  } catch (e) {
    log.error("Error running engine", e);
  }
};

/**
 * The entry the colour set-filter shows for rows the worker has not stamped. It is a
 * display label rather than a colour, so it has no counterpart in OpenSearch.
 */
export const NO_COLOR_FILTER_VALUE = "No Color";

/**
 * DP-3118: rewrites a colour set-filter so it can be sent to the server.
 *
 * `createCondition` turns a `set` filter into an OR of `matchPhrase` clauses, except for
 * AG_GRID_NOT_EXIST_FILTER, which becomes `exists: false`. Passing "No Color" through
 * unchanged therefore produces `matchPhrase: "No Color"` — a clause that matches nothing
 * instead of matching the unstamped rows. Measured on dev jobs_v5: the literal returns 0
 * documents where `exists: false` returns 16,030.
 *
 * The client-side equivalent (sortAndFilterByColors) maps the same label to `undefined`,
 * which is right for its Array.includes test against unstamped rows and wrong here.
 *
 * Returns a copy — the grid's own filter model must not be mutated.
 */
export const toServerColorFilter = (modelFilter: any) => ({
  ...modelFilter,
  values: (modelFilter?.values ?? []).map((value: string | null) =>
    value === NO_COLOR_FILTER_VALUE ? AG_GRID_NOT_EXIST_FILTER : value
  )
});

/**
 * DP-3216: the direction an `indicatorRank` sort must be sent to the server in.
 *
 * It is the OPPOSITE of what the user picked, and that is deliberate — do not "fix" it.
 *
 * Rank 1 is the MOST important colour, so ascending by rank number is descending by importance.
 * The client path has always meant the other thing: it sorts on the rule priority that
 * DispatchRulesDataProvider derives by INVERTING rank (`sorted.sort((a, b) => b.rank - a.rank)`
 * then `priority = ++rank`, so rank 8 -> priority 1), which makes ascending least-important-first.
 * Sending the user's direction straight through made turning `backendColorRules` on silently
 * reverse every colour sort in the app.
 *
 * Unstamped rows fall out of this correctly too: the server treats a missing rank as 999999, i.e.
 * least important, so they lead an ascending sort and trail a descending one — matching the
 * client, which scores them 0.
 */
export const toServerRankSortAscending = (userChoseAscending: boolean): boolean => !userChoseAscending;
